import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DashboardModal from './DashboardModal'
import type { ActionButton } from './DashboardModal'
import type { PmsJobCombinedDto, PmsCommandCenterDto, PmsActionCenterDto, RunningHourCounterDto } from '../../../core/pms/_models'
import type { PurchaseOrder, Requisition, GoodsReceiptListRow } from '../../../../Procurement/core/_models'
import type { PmsDerivedKpis } from './types'
import { useAuth } from '../../../../auth'

// ── Helpers ──
const fmtDate = (d?: string | null) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const priorityBadge = (c?: string | null) => {
  const map: Record<string, string> = { CRITICAL: 'bg-red-100 text-red-600', HIGH: 'bg-red-100 text-red-600', MEDIUM: 'bg-amber-100 text-amber-600', LOW: 'bg-slate-100 text-slate-600' }
  return map[c || ''] || 'bg-slate-100 text-slate-600'
}

const statusBadge = (s?: string | null) => {
  if (!s) return 'bg-slate-100 text-slate-600'
  if (['COMPLETED', 'DELIVERED', 'FULFILLED', 'CLOSED', 'POSTED'].includes(s)) return 'bg-emerald-100 text-emerald-600'
  if (['REJECTED', 'CANCELLED', 'OVERDUE'].includes(s)) return 'bg-red-100 text-red-600'
  if (s.includes('PARTIAL')) return 'bg-amber-100 text-amber-600'
  return 'bg-blue-100 text-blue-600'
}

function downloadCsv(filename: string, headers: string[], rows: string[][]) {
  const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

// ── Modal Props ──
interface OverdueJobsModalProps { open: boolean; onClose: () => void; jobs: PmsJobCombinedDto[] }
interface UpcomingJobsModalProps { open: boolean; onClose: () => void; jobs: PmsJobCombinedDto[] }
interface RunningHoursModalProps { open: boolean; onClose: () => void; counters: RunningHourCounterDto[] }
interface ProcurementDetailModalProps { open: boolean; onClose: () => void; recentPOs: PurchaseOrder[]; recentReqs: Requisition[]; recentGRNs: GoodsReceiptListRow[]; openPRCount: number; openPOCount: number; grnPendingCount: number }
interface KpiDetailModalProps { open: boolean; onClose: () => void; kpis: PmsDerivedKpis; stats: PmsCommandCenterDto | null; actionCenter: PmsActionCenterDto | null }

// ── Kpi Detail Modal ──
export const KpiDetailModal: FC<KpiDetailModalProps> = ({ open, onClose, kpis, stats, actionCenter }) => {
  const headers = ['Metric', 'Value']
  const kpiRows = [
    ['Overdue Jobs', String(kpis.overdueJobs)],
    ['Closed (30 Days)', String(kpis.closedJobs)],
    ['Total Jobs', String(kpis.totalJobs)],
    ['Unplanned Overdue', String(kpis.unplannedOverdue)],
    ['Due Soon (30 Days)', String(stats?.dueSoonJobs ?? 0)],
    ['Overdue Defect Jobs', String(stats?.overdueDefectJobs ?? 0)],
  ]
  const actions: ActionButton[] = [
    { label: 'Download Summary', onClick: () => downloadCsv('pms-kpi-summary.csv', headers, kpiRows) },
  ]

  return (
    <DashboardModal open={open} onClose={onClose} title='PMS Dashboard KPIs' wide actions={actions}>
      <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
        {kpiRows.map(([label, value], i) => (
          <div key={i} className='bg-slate-50 rounded-xl p-4'>
            <div className='text-[10px] text-slate-500'>{label}</div>
            <div className='text-2xl font-bold text-slate-900 mt-1'>{value}</div>
          </div>
        ))}
      </div>
      <h4 className='text-sm font-bold text-slate-900 mt-6 mb-3'>Action Center</h4>
      <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
        <div className='bg-slate-50 rounded-xl p-4'>
          <div className='text-[10px] text-slate-500'>Overdue Jobs</div>
          <div className='text-xl font-bold text-red-600 mt-1'>{actionCenter?.overdueJobs ?? 0}</div>
        </div>
        <div className='bg-slate-50 rounded-xl p-4'>
          <div className='text-[10px] text-slate-500'>Critical Equipments</div>
          <div className='text-xl font-bold text-amber-600 mt-1'>{actionCenter?.criticalEquipments ?? 0}</div>
        </div>
        <div className='bg-slate-50 rounded-xl p-4'>
          <div className='text-[10px] text-slate-500'>Pending Defect Requests</div>
          <div className='text-xl font-bold text-rose-600 mt-1'>{actionCenter?.pendingDefectRequests ?? 0}</div>
        </div>
        <div className='bg-slate-50 rounded-xl p-4'>
          <div className='text-[10px] text-slate-500'>Pending Postponements</div>
          <div className='text-xl font-bold text-indigo-600 mt-1'>{actionCenter?.pendingPostponements ?? 0}</div>
        </div>
        <div className='bg-slate-50 rounded-xl p-4'>
          <div className='text-[10px] text-slate-500'>Counters Never Started</div>
          <div className='text-xl font-bold text-slate-600 mt-1'>{actionCenter?.countersNeverStarted ?? 0}</div>
        </div>
      </div>
    </DashboardModal>
  )
}

// ── Overdue Jobs Modal ──
export const OverdueJobsModal: FC<OverdueJobsModalProps> = ({ open, onClose, jobs }) => {
  const navigate = useNavigate()
  const {currentUser, auth} = useAuth()
  const roleId = Number((auth?.userDetails as any)?.roleId ?? currentUser?.role?.id ?? 0) || 0
  const isCrew = roleId === 4

  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = jobs.filter((j) =>
    (j.job.vesselName || '').toLowerCase().includes(q) ||
    (j.job.equipmentName || '').toLowerCase().includes(q) ||
    (j.job.title || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    { label: 'Download CSV', onClick: () => downloadCsv('overdue-jobs.csv',
      isCrew
        ? ['Equipment', 'Job Code', 'Title', 'Due Date', 'Priority']
        : ['Vessel', 'Equipment', 'Job Code', 'Title', 'Due Date', 'Priority'],
      filtered.map((j) => isCrew
        ? [j.job.equipmentName || '', j.job.jobCode || '', j.job.title || '', fmtDate(j.job.dueDate), j.job.criticality || 'N/A']
        : [j.job.vesselName || '', j.job.equipmentName || '', j.job.jobCode || '', j.job.title || '', fmtDate(j.job.dueDate), j.job.criticality || 'N/A']
      ),
    )},
    { label: 'View All Jobs', variant: 'primary', onClick: () => navigate('/technical/pms') },
  ]

  return (
    <DashboardModal
      open={open} onClose={onClose} title='Overdue Jobs' wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by vessel, equipment or title...'
    >
      {filtered.length === 0 ? (
        <p className='text-center text-slate-400 py-6'>No overdue jobs</p>
      ) : (
        <table className='w-full text-left text-[11px]'>
          <thead>
            <tr className='text-slate-400 uppercase border-b border-slate-200'>
              {!isCrew && <th className='pb-2 font-medium'>Vessel</th>}
              <th className='pb-2 font-medium'>Equipment</th>
              <th className='pb-2 font-medium'>Job Code</th>
              <th className='pb-2 font-medium'>Title</th>
              <th className='pb-2 font-medium'>Due Date</th>
              <th className='pb-2 font-medium'>Priority</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((j) => (
              <tr key={j.job.id} className='border-t border-slate-50'>
                {!isCrew && <td className='py-2 font-medium text-slate-800'>{j.job.vesselName || '—'}</td>}
                <td className='py-2 text-slate-600'>{j.job.equipmentName || '—'}</td>
                <td className='py-2 text-slate-600'>{j.job.jobCode || '—'}</td>
                <td className='py-2 text-slate-700'>{j.job.title || '—'}</td>
                <td className='py-2 text-slate-800'>{fmtDate(j.job.dueDate)}</td>
                <td className='py-2'>
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${priorityBadge(j.job.criticality)}`}>
                    {j.job.criticality || 'N/A'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </DashboardModal>
  )
}

// ── Upcoming Jobs Modal ──
export const UpcomingJobsModal: FC<UpcomingJobsModalProps> = ({ open, onClose, jobs }) => {
  const navigate = useNavigate()
  const {currentUser, auth} = useAuth()
  const roleId = Number((auth?.userDetails as any)?.roleId ?? currentUser?.role?.id ?? 0) || 0
  const isCrew = roleId === 4

  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = jobs.filter((j) =>
    (j.job.vesselName || '').toLowerCase().includes(q) ||
    (j.job.equipmentName || '').toLowerCase().includes(q) ||
    (j.job.title || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    { label: 'Download CSV', onClick: () => downloadCsv('upcoming-jobs.csv',
      isCrew
        ? ['Equipment', 'Title', 'Due Date', 'Priority']
        : ['Vessel', 'Equipment', 'Title', 'Due Date', 'Priority'],
      filtered.map((j) => isCrew
        ? [j.job.equipmentName || '', j.job.title || '', fmtDate(j.job.dueDate), j.job.criticality || 'N/A']
        : [j.job.vesselName || '', j.job.equipmentName || '', j.job.title || '', fmtDate(j.job.dueDate), j.job.criticality || 'N/A']
      ),
    )},
    { label: 'View All Jobs', variant: 'primary', onClick: () => navigate('/technical/pms') },
  ]

  return (
    <DashboardModal
      open={open} onClose={onClose} title='Upcoming Jobs' wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by vessel, equipment or title...'
    >
      {filtered.length === 0 ? (
        <p className='text-center text-slate-400 py-6'>No upcoming jobs</p>
      ) : (
        <table className='w-full text-left text-[11px]'>
          <thead>
            <tr className='text-slate-400 uppercase border-b border-slate-200'>
              {!isCrew && <th className='pb-2 font-medium'>Vessel</th>}
              <th className='pb-2 font-medium'>Equipment</th>
              <th className='pb-2 font-medium'>Title</th>
              <th className='pb-2 font-medium'>Due Date</th>
              <th className='pb-2 font-medium'>Priority</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((j) => (
              <tr key={j.job.id} className='border-t border-slate-50'>
                {!isCrew && <td className='py-2 font-medium text-slate-800'>{j.job.vesselName || '—'}</td>}
                <td className='py-2 text-slate-600'>{j.job.equipmentName || '—'}</td>
                <td className='py-2 text-slate-700'>{j.job.title || '—'}</td>
                <td className='py-2 text-slate-800'>{fmtDate(j.job.dueDate)}</td>
                <td className='py-2'>
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${priorityBadge(j.job.criticality)}`}>
                    {j.job.criticality || 'N/A'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </DashboardModal>
  )
}

// ── Running Hours Modal ──
export const RunningHoursModal: FC<RunningHoursModalProps> = ({ open, onClose, counters }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()
  const filtered = counters.filter((c) =>
    (c.equipmentName || c.name || '').toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    { label: 'Download CSV', onClick: () => downloadCsv('running-hours.csv',
      ['Equipment', 'Hours', 'Last Reading'],
      filtered.map((c) => [c.equipmentName || c.name || '', String(c.currentValue), fmtDate(c.lastReadingDate)]),
    )},
  ]

  return (
    <DashboardModal
      open={open} onClose={onClose} title='Running Hours Counters' wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by equipment name...'
    >
      {filtered.length === 0 ? (
        <p className='text-center text-slate-400 py-6'>No running hour counters</p>
      ) : (
        <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3'>
          {filtered.map((c) => (
            <div key={c.id} className='bg-slate-50 rounded-xl p-3 border border-slate-100'>
              <div className='text-[10px] text-slate-500 truncate' title={c.equipmentName || c.name}>
                {c.equipmentName || c.name}
              </div>
              <div className='text-lg font-bold text-slate-900 mt-0.5'>
                {c.currentValue.toLocaleString()}<span className='text-[10px] font-normal text-slate-400 ml-0.5'>hrs</span>
              </div>
              <div className='text-[9px] text-slate-400 mt-0.5'>
                {c.lastReadingDate ? fmtDate(c.lastReadingDate) : 'No reading'}
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardModal>
  )
}

// ── Procurement Detail Modal ──
export const ProcurementDetailModal: FC<ProcurementDetailModalProps> = ({ open, onClose, recentPOs, recentReqs, recentGRNs, openPRCount, openPOCount, grnPendingCount }) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const q = searchQuery.toLowerCase()

  const filteredPOs = recentPOs.filter((po) =>
    (po.poNumber || '').toLowerCase().includes(q) ||
    (po.vendor?.name || '').toLowerCase().includes(q)
  )
  const filteredReqs = recentReqs.filter((r) =>
    (r.requisition_code || '').toLowerCase().includes(q) ||
    (r.title || '').toLowerCase().includes(q)
  )
  const filteredGRNs = recentGRNs.filter((g) =>
    (g.requisitionNo || `PO-${g.purchaseOrderId}`).toLowerCase().includes(q)
  )

  const actions: ActionButton[] = [
    { label: 'Download CSV', onClick: () => {
      const rows: string[][] = []
      filteredPOs.forEach((po) => rows.push(['PO', po.poNumber || `PO-${po.id}`, po.vendor?.name || '', po.totalAmount?.toLocaleString() || '', po.status || '']))
      filteredReqs.forEach((r) => rows.push(['Requisition', r.requisition_code || `PR-${r.id}`, r.title || '', '', r.status || '']))
      filteredGRNs.forEach((g) => rows.push(['GRN', `GRN-${g.id}`, g.requisitionNo || `PO-${g.purchaseOrderId}`, '', g.status || '']))
      downloadCsv('procurement.csv', ['Type', 'Number', 'Vendor/Title', 'Amount', 'Status'], rows)
    }},
    { label: 'View Procurement', variant: 'primary', onClick: () => navigate('/technical/procurement') },
  ]

  return (
    <DashboardModal
      open={open} onClose={onClose} title='Procurement Details' wide
      actions={actions}
      searchQuery={searchQuery} onSearchChange={setSearchQuery} searchPlaceholder='Search by PO, requisition or vendor...'
    >
      {/* Purchase Orders */}
      <h4 className='text-sm font-bold text-slate-900 mb-2'>Purchase Orders <span className='text-slate-400 font-normal'>({openPOCount})</span></h4>
      <div className='overflow-x-auto mb-5'>
        <table className='w-full text-left text-[11px]'>
          <thead>
            <tr className='text-slate-400 uppercase border-b border-slate-200'>
              <th className='pb-2 font-medium'>PO No.</th>
              <th className='pb-2 font-medium'>Vendor</th>
              <th className='pb-2 font-medium'>Amount</th>
              <th className='pb-2 font-medium'>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredPOs.length === 0 ? (
              <tr><td colSpan={4} className='py-4 text-center text-slate-400'>No purchase orders</td></tr>
            ) : filteredPOs.map((po) => (
              <tr key={po.id} className='border-t border-slate-50'>
                <td className='py-2 font-medium text-slate-800'>{po.poNumber || `PO-${po.id}`}</td>
                <td className='py-2 text-slate-600'>{po.vendor?.name || '—'}</td>
                <td className='py-2 text-slate-800'>{po.totalAmount != null ? `$ ${po.totalAmount.toLocaleString()}` : '—'}</td>
                <td className='py-2'><span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${statusBadge(po.status)}`}>{po.status?.replace(/_/g, ' ')}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Requisitions */}
      <h4 className='text-sm font-bold text-slate-900 mb-2'>Requisitions <span className='text-slate-400 font-normal'>({openPRCount})</span></h4>
      <div className='overflow-x-auto mb-5'>
        <table className='w-full text-left text-[11px]'>
          <thead>
            <tr className='text-slate-400 uppercase border-b border-slate-200'>
              <th className='pb-2 font-medium'>Req No.</th>
              <th className='pb-2 font-medium'>Title</th>
              <th className='pb-2 font-medium'>Required By</th>
              <th className='pb-2 font-medium'>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredReqs.length === 0 ? (
              <tr><td colSpan={4} className='py-4 text-center text-slate-400'>No requisitions</td></tr>
            ) : filteredReqs.map((r) => (
              <tr key={r.id} className='border-t border-slate-50'>
                <td className='py-2 font-medium text-slate-800'>{r.requisition_code || `PR-${r.id}`}</td>
                <td className='py-2 text-slate-600 max-w-[200px] truncate'>{r.title || '—'}</td>
                <td className='py-2 text-slate-800'>{fmtDate(r.requiredDeliveryDate)}</td>
                <td className='py-2'><span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${statusBadge(r.status)}`}>{r.status?.replace(/_/g, ' ')}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* GRN */}
      <h4 className='text-sm font-bold text-slate-900 mb-2'>Goods Receipts <span className='text-slate-400 font-normal'>({grnPendingCount})</span></h4>
      <div className='overflow-x-auto'>
        <table className='w-full text-left text-[11px]'>
          <thead>
            <tr className='text-slate-400 uppercase border-b border-slate-200'>
              <th className='pb-2 font-medium'>GRN No.</th>
              <th className='pb-2 font-medium'>PO No.</th>
              <th className='pb-2 font-medium'>Received</th>
              <th className='pb-2 font-medium'>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredGRNs.length === 0 ? (
              <tr><td colSpan={4} className='py-4 text-center text-slate-400'>No goods receipts</td></tr>
            ) : filteredGRNs.map((g) => (
              <tr key={g.id} className='border-t border-slate-50'>
                <td className='py-2 font-medium text-slate-800'>{`GRN-${g.id}`}</td>
                <td className='py-2 text-slate-600'>{g.requisitionNo || `PO-${g.purchaseOrderId}`}</td>
                <td className='py-2 text-slate-600'>{fmtDate(g.receivedDate)}</td>
                <td className='py-2'><span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${statusBadge(g.status)}`}>{g.status?.replace(/_/g, ' ')}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardModal>
  )
}
